/**
 * DICE.app's notation composer — the pure half, kept out of {@link DiceApp} so the
 * arithmetic is unit-testable without mounting React.
 *
 * Composes the one roll Lancer's narrative play calls for: `1d20 + <bonus> ± <accuracy or
 * difficulty>`, into a `/roll …` string ready for `session.postChat` (COMMS.app's `/roll`
 * command, `backend/src/gameRoom.ts`'s `postChat`, parsed by `backend/src/diceNotation.ts`'s
 * compound parser). The output is always exactly what gets sent — DICE.app's live preview and
 * its ROLL button both read straight off this function's return value.
 *
 * **Cancellation happens here, as arithmetic on the counts, BEFORE any string gets built.**
 * Accuracy and difficulty are opposed d6 pools that cancel 1-for-1 — 2 accuracy + 1 difficulty
 * is net **+1 accuracy**, a single `1d6kh1` pool, never `+2d6kh1-1d6kh1` (two pools rolled and
 * subtracted is a genuinely different probability distribution, not a formatting choice). A net
 * of zero emits no dice term at all. The signed net's magnitude becomes the pool size; its sign
 * picks `+` or `-`; the pool is *always* `kh1` (keep the single highest die), because Lancer's
 * accuracy/difficulty rule is "roll one extra d6 per net accuracy/difficulty and take the best,"
 * not "roll a shrinking pool and keep low" — reaching for `kl` here would be a different rule.
 * The rulebook's "no roll can ever receive more than ±6" is a *consequence* of that `kh1` (a
 * single d6 face tops out at 6), not a separate constraint, so there is deliberately no clamp
 * on the net here — clamping would be dead code standing in for a rule this function already
 * embodies for free.
 *
 * The base `1d20` term is unconditional (a check is always a d20 test) and is what guarantees
 * the output always has at least one dice term, satisfying the backend parser's "a roll needs at
 * least one dice term" floor even when bonus/net are both zero.
 */

/**
 * Compose a `/roll …` string for `1d20 + bonus ± (accuracy − difficulty)`.
 *
 * @param bonus A flat integer added to the check (a HASE skill, a trigger rank, a generic GM
 *   bonus) — positive, negative or zero. Zero emits no bonus term.
 * @param accuracy Non-negative count of accuracy dice.
 * @param difficulty Non-negative count of difficulty dice. Cancels 1-for-1 against `accuracy`
 *   before either is rendered — see the module doc.
 * @returns The exact string to hand to `session.postChat` — e.g. `"/roll 1d20+2+1d6kh1"`.
 */
export function composeCheckNotation(bonus: number, accuracy: number, difficulty: number): string {
  const net = accuracy - difficulty;

  let notation = "1d20";
  if (bonus !== 0) notation += bonus > 0 ? `+${bonus}` : `${bonus}`;
  if (net !== 0) {
    const pool = Math.abs(net);
    notation += net > 0 ? `+${pool}d6kh1` : `-${pool}d6kh1`;
  }

  return `/roll ${notation}`;
}

/**
 * Name a `/roll` command: append ` # <label>` so the COMMS card and the roll toast can say what it
 * was for (`backend/src/gameRoom.ts`'s `postRoll`). A missing or blank label leaves it unnamed.
 */
export function labelRoll(roll: string, label: string | null | undefined): string {
  const name = label?.trim();
  return name ? `${roll} # ${name}` : roll;
}
